import React, { useEffect, useState, useCallback } from "react";
import {
  Typography,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  makeStyles,
  DialogActions
} from "@material-ui/core";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { GRAY, LIGHTER_GRAY, LIGHT_GRAY, PRIMARY_COLOR, WHITE } from "../../../../theme";
import createValidationSchema from "./createValidationSchema";
import GeneralSegment from "./GeneralSegment";
import LicenseSegment from "./LicenseSegment";
import CourseSegment from "./CourseSegment";
import TermsSegment from "./TermsSegment";
import ActionSegment from "./ActionSegment";
import ConfirmationModal from "../../../../shared/components/ConfirmationModal";
import { messages, adUserSize } from "../../../../shared/utils/Constants";
import useCreateOrUpdateUser, { userModal } from "../../customHooks/useCreateOrUpdateUser";
import { cnhIsRequired } from "../../../../shared/utils/Utils";
import { driverProfiles, userManagementProfiles } from "../../../../shared/utils/enum";
import EditionSegment from "../../../../shared/components/ManagementPage/EditionSegment";
import CloseIcon from "../../../../shared/components/CloseIcon";
import PoleService from "../../../../services/PoleService";
import { mapUserFormToPayload } from "./userPayload";

const RegistrationModal = props => {
  const { isOpen, handleClose, listUsersFiltered, editableUserId, setEditableUserId } = props;
  const classes = useStyles();
  const defaultValues = userModal.defaultValues;
  const isEdition = !!editableUserId;

  const [openedCancelModal, setOpenedCancelModal] = useState(false);
  const [openedDropModal, setOpenedDropModal] = useState(false);
  const [openedConfirmationModal, setOpenedConfirmationModal] = useState(false);
  const [confirmationModalTitle, setConfirmationModalTitle] = useState(
    messages.SUCCESS_CREATED_USER
  );
  const [oldUserData, setOldUserData] = useState(null);
  const [initialPoles, setInitialPoles] = useState([]);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, isValid, dirtyFields },
    reset,
    watch,
    setValue
  } = useForm({
    resolver: yupResolver(createValidationSchema()),
    mode: "onChange",
    defaultValues: defaultValues
  });

  const [selectedPoles, setSelectedPoles] = useState([]);

  const {
    getUserById,
    createOrUpdate,
    loadingCreateOrUpdate,
    loadingUser,
    setSchemaValues,
    checkIfDeletedCourseOrLicense,
    userData,
    loadUserData,
    resetUserData
  } = useCreateOrUpdateUser(setValue, editableUserId, setSelectedPoles);

  const [values, setValues] = useState(defaultValues);

  const handleChangeAdUser = useCallback(
    async adUser => {
      if (!adUser.length) resetUserData();

      if (adUser.length === adUserSize) {
        await loadUserData(adUser);
      }
    },
    [loadUserData, resetUserData]
  );


  useEffect(() => {
    const subscription = watch(async (updatedValues, { name, type }) => {
      setValues(updatedValues);

      if (type !== "change") return;

      if (name === "adUser") {
        handleChangeAdUser(updatedValues.adUser);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, setValue, defaultValues, handleChangeAdUser]);

  useEffect(() => {
    async function getOldUserData() {
      const callback = (data, currentPoles) => {
        setSchemaValues(data);
        setOldUserData(data);
        setInitialPoles(currentPoles);
      };

      await getUserById(callback);
    }

    if (isOpen) {
      setConfirmationModalTitle(messages.SUCCESS_CREATED_USER);
      if (isEdition) {
        setConfirmationModalTitle(messages.SUCCESS_UPDATED_USER);
        getOldUserData();
      }
    } else {
      reset();
      resetUserData();
      setEditableUserId(null);
      setOldUserData(null);
      setInitialPoles([]);
    }
  }, [isOpen, isEdition, getUserById, reset, resetUserData, setEditableUserId, setSchemaValues]);

  const [poleList, setPoleList] = useState([]);

  useEffect(() => {
    const loadPoles = async () => {
      PoleService.listPole().then(response => {
        setPoleList(response.data.map(el => {
          return {
            label: el.name,
            value: el.id
          }
        }));
      }).catch(() => {
        console.error("Erro ao listar os polos");
      });
    }

    loadPoles();
  }, []);

  const submitForm = async data => {
    const callback = () => {
      reset();
      handleClose();
      setOpenedConfirmationModal(true);
      listUsersFiltered();
    };

    const isDeletedLicenseOrCourse =
      !!editableUserId && checkIfDeletedCourseOrLicense(data, oldUserData);

    if (isDeletedLicenseOrCourse) {
      setOpenedDropModal(true);
      return;
    }

    if (!isEdition) {
      const primaryPoleName = selectedPoles[0];
      const primaryPoleId = poleList.find(p => p.label === primaryPoleName)?.value;
      const user = {
        adUser: data.adUser,
        profile: { profileId: data.profileId },
        pole: primaryPoleId ? { id: primaryPoleId } : null,
        license: { licenseTypeId: data.licenseTypeId > 0 ? data.licenseTypeId : null, expireDate: data.licenseExpireDate },
        course: { expireDate: data.courseExpireDate },
        termsAcceptedAt: data.termsAcceptedAt
      };
      await createOrUpdate(user, data, async () => {
        const isRegionalFac = values.profileId === userManagementProfiles.regionalFac.value;
        if (isRegionalFac) {
          const secondaryPoles = selectedPoles.slice(1);
          const secondaryPoleIds = secondaryPoles.map(name => poleList.find(p => p.label === name)?.value).filter(id => id !== undefined);
          if (secondaryPoles.length > 0) {
            await PoleService.AddMultiplePolesToUser(data.adUser, secondaryPoleIds);
          }
        }
        callback();
      });
    } else {
      const polesHaveChanged = JSON.stringify(initialPoles.sort()) !== JSON.stringify(selectedPoles.sort());
      const otherFieldsAreDirty = Object.keys(dirtyFields).some(field => field !== 'reasonForEdit');

      if (!otherFieldsAreDirty && !polesHaveChanged) {
        handleClose();
        return;
      }

      if (otherFieldsAreDirty) {
        const user = mapUserFormToPayload(data);
        await createOrUpdate(user, data, () => {});
      }

      if (polesHaveChanged) {
        const addedPoles = selectedPoles.filter(p => !initialPoles.includes(p));
        const removedPoles = initialPoles.filter(p => !selectedPoles.includes(p));
    
        const addedPoleIds = addedPoles
            .map(name => poleList.find(p => p.label === name)?.value)
            .filter(Boolean);
        
        const removedPoleIds = removedPoles
            .map(name => poleList.find(p => p.label === name)?.value)
            .filter(Boolean);
    
        const promises = [];
        if (addedPoleIds.length > 0) {
            promises.push(PoleService.AddMultiplePolesToUser(data.adUser, addedPoleIds));
        }
        if (removedPoleIds.length > 0) {
            promises.push(PoleService.deleteUserPoles(data.adUser, removedPoleIds));
        }
        
        await Promise.all(promises);
      }
      
      callback();
    }
  };

  const handleCancelUserRegistration = () => {
    handleClose();
    setOpenedCancelModal(false);
  };

  const polesHaveChanged = JSON.stringify(initialPoles.sort()) !== JSON.stringify(selectedPoles.sort());

  return (
    <>
      <form aria-label="Formulário cadastro usuário" noValidate onSubmit={handleSubmit(submitForm)}>
        <Dialog
          fullWidth
          open={isOpen}
          onClose={handleClose}
          className={classes.modal}
          classes={{
            paper: classes.paperModal
          }}
        >
          <CloseIcon color={WHITE} onClick={handleClose} />

          <DialogTitle
            disableTypography
            onClose={() => handleClose()}
            className={classes.titleContainer}
          >
            <Typography className={classes.modalTitle} align="center" variant="h6">
              {!!editableUserId ? messages.UPDATE_USER : messages.REGISTER_USER}
            </Typography>
          </DialogTitle>

          <DialogContent>
            <Grid item xs={12} className="mt-1">
              <GeneralSegment
                control={control}
                errors={errors}
                values={values}
                isEdition={isEdition}
                userData={userData}
                userDataIsLoading={loadingUser}
                isEditableUser={!!editableUserId}
                selectedPoles={selectedPoles}
                setSelectedPoles={setSelectedPoles}
                poleList={poleList}
              />
            </Grid>
            <Grid item container xs={12} className="mb-6">
              <LicenseSegment
                control={control}
                errors={!isValid && errors}
                values={values}
                licenseTypeIsRequired={cnhIsRequired(
                  values.profileId,
                  null,
                  values.licenseExpireDate
                )}
                expireDateIsRequired={cnhIsRequired(
                  values.profileId,
                  values.licenseTypeId,
                  values.licenseExpireDate
                )}
              />
              <CourseSegment
                control={control}
                errors={!isValid && errors}
                required={driverProfiles.includes(values.profileId)}
              />
            </Grid>
            <Grid item container xs={12} className="mb-6">
              <TermsSegment control={control} errors={!isValid && errors} />
            </Grid>
            {isEdition && (
              <Grid item container xs={12}>
                <EditionSegment control={control} errors={errors} />
              </Grid>
            )}
            <Typography variant="caption" className={classes.caption}>
              *campos obrigatórios
            </Typography>
          </DialogContent>

          <DialogActions>
            <ActionSegment
              classes={classes}
              secondaryAction={() => setOpenedCancelModal(true)}
              loading={loadingCreateOrUpdate}
              handleSubmit={handleSubmit(submitForm)}
              disableSubmit={(!isDirty && !polesHaveChanged) || !isValid || !userData || selectedPoles.length === 0}
              isEditableUser={!!editableUserId}
            />
          </DialogActions>
        </Dialog>
      </form>

      <ConfirmationModal
        shouldCancel
        openedModal={openedCancelModal}
        title={
          !!editableUserId
            ? messages.CONFIRMATION_CANCEL_EDIT_TITLE
            : messages.CONFIRMATION_CANCEL_CREATE_TITLE
        }
        subtitle={
          !!editableUserId
            ? messages.CONFIRMATION_CANCEL_EDIT_SUBTITLE
            : messages.CONFIRMATION_CANCEL_CREATE_SUBTITLE
        }
        handleOpenClose={() => setOpenedCancelModal(false)}
        secondaryAction={() => setOpenedCancelModal(false)}
        primaryAction={handleCancelUserRegistration}
        secondaryButtonText={messages.NO_BUTTON_MESSAGE}
        primaryButtonText={messages.YES_BUTTON_MESSAGE}
      />

      <ConfirmationModal
        shouldCancel
        openedModal={openedDropModal}
        title={messages.ALERT_TITLE}
        subtitle={messages.ALERT_DROP_SUBTITLE}
        handleOpenClose={() => setOpenedDropModal(false)}
        primaryAction={() => setOpenedDropModal(false)}
        primaryButtonText={messages.OK_BUTTON_MESSAGE}
      />

      <ConfirmationModal
        success
        openedModal={openedConfirmationModal}
        title={confirmationModalTitle}
        handleOpenClose={() => setOpenedConfirmationModal(false)}
        primaryAction={() => setOpenedConfirmationModal(false)}
        primaryButtonText={messages.OK_BUTTON_MESSAGE}
      />
    </>
  );
};

const useStyles = makeStyles(() => ({
  modal: {
    overflow: "hidden",
    width: "100%",
    maxHeight: "100%",

    "& .MuiDialogActions-root": {
      boxShadow: `0px -1px 8px ${LIGHT_GRAY}`
    },

    "& .MuiDialogContent-root": {
      backgroundColor: LIGHTER_GRAY
    },

    "& .MuiInputBase-root": {
      backgroundColor: WHITE
    },

    "& .MuiInputBase-root.Mui-disabled": {
      backgroundColor: LIGHTER_GRAY
    },

    "& .MuiInputBase-input.Mui-disabled": {
      color: GRAY
    }
  },
  caption: {
    color: GRAY
  },
  paperModal: {
    backgroundColor: WHITE,
    maxWidth: "50%"
  },
  titleContainer: {
    backgroundColor: PRIMARY_COLOR,
    textAlign: "center"
  },
  modalTitle: {
    color: WHITE
  }
}));

export default RegistrationModal;
